import { api } from './client';

export const adminApi = {
  // === 개인정보 접근 로그 ===
  getAccessLogSummary: async (): Promise<any> => {
    const response = await api.get('/api/access-logs/summary');
    return response.data;
  },

  getAccessLogAlerts: async (): Promise<any> => {
    const response = await api.get('/api/access-logs/alerts');
    return response.data;
  },

  getAccessLogs: async (params?: { username?: string; resource_type?: string; start_date?: string; end_date?: string; limit?: number; offset?: number }): Promise<any> => {
    const response = await api.get('/api/access-logs/', { params: params || {} });
    return response.data;
  },

  purgeAccessLogs: async (retentionDays?: number): Promise<any> => {
    const response = await api.post('/api/access-logs/purge', null, { params: retentionDays ? { retention_days: retentionDays } : {} });
    return response.data;
  },

  // === 감사 로그 ===
  getAuditSummary: async (): Promise<any> => {
    const response = await api.get('/api/audit/summary');
    return response.data;
  },

  getAuditLogs: async (params?: { username?: string; action?: string; resource_type?: string; resource_id?: string; start_date?: string; end_date?: string; limit?: number; offset?: number }): Promise<any> => {
    const response = await api.get('/api/audit/logs', { params: params || {} });
    return response.data;
  },

  getAuditTrail: async (resourceType: string, resourceId: string, limit?: number): Promise<any> => {
    const response = await api.get(`/api/audit/trail/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`, { params: limit ? { limit } : {} });
    return response.data;
  },

  getAuditLabels: async (): Promise<any> => {
    const response = await api.get('/api/audit/labels');
    return response.data;
  },

  // === 장애인 고용 의무 ===
  getDisabilityEmploymentSummary: async (totalEmployees?: number): Promise<any> => {
    const response = await api.get('/api/disability-employment/summary', { params: totalEmployees ? { total_employees: totalEmployees } : {} });
    return response.data;
  },

  getDisabilityEmployees: async (): Promise<any> => {
    const response = await api.get('/api/disability-employment/employees');
    return response.data;
  },

  createDisabilityEmployee: async (data: { employee_id: string; disability_type: string; disability_grade?: string; hire_date?: string; department?: string; notes?: string }): Promise<any> => {
    const response = await api.post('/api/disability-employment/', data);
    return response.data;
  },

  updateDisabilityEmployee: async (recordId: number, data: { disability_type?: string; disability_grade?: string; hire_date?: string; department?: string; notes?: string }): Promise<any> => {
    const response = await api.put(`/api/disability-employment/${recordId}`, data);
    return response.data;
  },

  // === 산업안전보건 ===
  getSafetyEducationSummary: async (): Promise<any> => {
    const response = await api.get('/api/safety-health/education/summary');
    return response.data;
  },

  getSafetyEducations: async (params?: { employee_id?: string; education_type?: string }): Promise<any> => {
    const response = await api.get('/api/safety-health/education', { params: params || {} });
    return response.data;
  },

  createSafetyEducation: async (data: { employee_id: string; education_type: string; education_name: string; education_date?: string; education_hours?: number; provider?: string; is_completed?: boolean; notes?: string }): Promise<any> => {
    const response = await api.post('/api/safety-health/education', data);
    return response.data;
  },

  getHealthCheckupSummary: async (): Promise<any> => {
    const response = await api.get('/api/safety-health/checkup/summary');
    return response.data;
  },

  getHealthCheckups: async (params?: { employee_id?: string; checkup_type?: string }): Promise<any> => {
    const response = await api.get('/api/safety-health/checkup', { params: params || {} });
    return response.data;
  },

  createHealthCheckup: async (data: { employee_id: string; checkup_type: string; checkup_date?: string; next_due_date?: string; result?: string; institution?: string; notes?: string }): Promise<any> => {
    const response = await api.post('/api/safety-health/checkup', data);
    return response.data;
  },

  getSafetyHealthAlerts: async (): Promise<any> => {
    const response = await api.get('/api/safety-health/alerts');
    return response.data;
  },

  // === 개인정보 보유기간 관리 ===
  getRetentionPolicies: async (): Promise<any> => {
    const response = await api.get('/api/data-retention/policies');
    return response.data;
  },

  updateRetentionPolicy: async (dataType: string, retentionYears: number): Promise<any> => {
    const response = await api.put(`/api/data-retention/policies/${dataType}`, { retention_years: retentionYears });
    return response.data;
  },

  getRetentionSummary: async (): Promise<any> => {
    const response = await api.get('/api/data-retention/summary');
    return response.data;
  },

  getExpiredEmployees: async (): Promise<any> => {
    const response = await api.get('/api/data-retention/expired');
    return response.data;
  },

  destroyEmployeeData: async (employeeId: string, dataType: string, method: string = 'anonymization'): Promise<any> => {
    const response = await api.post('/api/data-retention/destroy', { employee_id: employeeId, data_type: dataType, method });
    return response.data;
  },

  getDestructionLog: async (limit: number = 20, offset: number = 0): Promise<any> => {
    const response = await api.get(`/api/data-retention/destruction-log?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  // === 정보주체 권리 (개인정보보호법) ===
  createDataSubjectRequest: async (data: { employee_id: string; request_type: string; details?: string }): Promise<any> => {
    const response = await api.post('/api/data-subject/request', data);
    return response.data;
  },

  getDataSubjectRequests: async (params?: { status?: string; request_type?: string }): Promise<any> => {
    const response = await api.get('/api/data-subject/requests', { params: params || {} });
    return response.data;
  },

  reviewDataSubjectRequest: async (requestId: number, data: { action: 'approve' | 'reject'; notes?: string }): Promise<any> => {
    const response = await api.put(`/api/data-subject/${requestId}/review`, data);
    return response.data;
  },

  getDataSubjectSummary: async (): Promise<any> => {
    const response = await api.get('/api/data-subject/summary');
    return response.data;
  },

  getDataSubjectTypes: async (): Promise<any> => {
    const response = await api.get('/api/data-subject/types');
    return response.data;
  },

  // === 고충처리 ===
  getGrievances: async (status?: string, category?: string, limit: number = 20, offset: number = 0): Promise<any> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const response = await api.get(`/api/grievances?${params}`);
    return response.data;
  },

  getGrievanceSummary: async (): Promise<any> => {
    const response = await api.get('/api/grievances/summary');
    return response.data;
  },

  createGrievance: async (data: { category: string; title: string; description: string; is_anonymous?: boolean; priority?: string }): Promise<any> => {
    const response = await api.post('/api/grievances', data);
    return response.data;
  },

  updateGrievance: async (gid: number, data: { status?: string; resolution?: string; assigned_name?: string }): Promise<any> => {
    const response = await api.patch(`/api/grievances/${gid}`, data);
    return response.data;
  },

  getGrievanceDetail: async (gid: number): Promise<any> => {
    const response = await api.get(`/api/grievances/${gid}`);
    return response.data;
  },

  // === 취업규칙/인사규정 ===
  getEmploymentRules: async (category?: string, status?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    const response = await api.get(`/api/employment-rules?${params}`);
    return response.data;
  },

  getEmploymentRulesSummary: async (): Promise<any> => {
    const response = await api.get('/api/employment-rules/summary');
    return response.data;
  },

  createEmploymentRule: async (data: { category: string; title: string; content: string }): Promise<any> => {
    const response = await api.post('/api/employment-rules', data);
    return response.data;
  },

  getEmploymentRuleDetail: async (ruleId: number): Promise<any> => {
    const response = await api.get(`/api/employment-rules/${ruleId}`);
    return response.data;
  },

  getEmploymentRuleVersionContent: async (ruleId: number, version: number): Promise<any> => {
    const response = await api.get(`/api/employment-rules/${ruleId}/versions/${version}/content`);
    return response.data;
  },

  updateEmploymentRuleStatus: async (ruleId: number, status: string, effectiveDate?: string): Promise<any> => {
    const response = await api.patch(`/api/employment-rules/${ruleId}/status`, { status, effective_date: effectiveDate });
    return response.data;
  },

  uploadEmploymentRuleWithFile: async (category: string, title: string, file: File, content?: string): Promise<any> => {
    const formData = new FormData();
    formData.append('category', category);
    formData.append('title', title);
    formData.append('content', content || '');
    formData.append('file', file);
    const response = await api.post('/api/employment-rules/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteEmploymentRule: async (ruleId: number): Promise<any> => {
    const response = await api.delete(`/api/employment-rules/${ruleId}`);
    return response.data;
  },

  getEmploymentRuleFileUrl: (ruleId: number, version?: number): string => {
    const base = api.defaults.baseURL || '';
    const v = version ? `?version=${version}` : '';
    return `${base}/api/employment-rules/${ruleId}/file${v}`;
  },

  // MYB-357: Feedback API removed — replaced by unified ticket system

  // === 대시보드 커스터마이징 ===
  getDashboardWidgets: async (): Promise<any> => {
    const response = await api.get('/api/dashboard/widgets');
    return response.data;
  },

  toggleDashboardWidget: async (widgetType: string, isVisible: boolean): Promise<any> => {
    const response = await api.put('/api/dashboard/widgets/visibility', { widget_type: widgetType, is_visible: isVisible });
    return response.data;
  },

  reorderDashboardWidget: async (widgetType: string, direction: string): Promise<any> => {
    const response = await api.put('/api/dashboard/widgets/reorder', { widget_type: widgetType, direction });
    return response.data;
  },

  bulkUpdateDashboardWidgets: async (widgets: any[]): Promise<any> => {
    const response = await api.put('/api/dashboard/widgets/bulk', { widgets });
    return response.data;
  },

  resetDashboardWidgets: async (): Promise<any> => {
    const response = await api.post('/api/dashboard/widgets/reset');
    return response.data;
  },

  // === 알림 (BACK-181) ===
  getNotificationPreferences: async (): Promise<any> => {
    const response = await api.get('/api/notifications/preferences');
    return response.data;
  },

  updateNotificationPreferences: async (preferences: any[]): Promise<any> => {
    const response = await api.put('/api/notifications/preferences', { preferences });
    return response.data;
  },

  getNotificationChannels: async (): Promise<any> => {
    const response = await api.get('/api/notifications/channels');
    return response.data;
  },

  getNotifications: async (unreadOnly?: boolean, limit?: number, offset?: number): Promise<any> => {
    const params: any = {};
    if (unreadOnly) params.unread_only = true;
    if (limit) params.limit = limit;
    if (offset) params.offset = offset;
    const response = await api.get('/api/notifications', { params });
    return response.data;
  },

  getUnreadNotificationCount: async (): Promise<{ count: number }> => {
    const response = await api.get('/api/notifications/unread-count');
    return response.data;
  },

  markNotificationRead: async (id: number): Promise<any> => {
    const response = await api.put(`/api/notifications/${id}/read`);
    return response.data;
  },

  markAllNotificationsRead: async (): Promise<any> => {
    const response = await api.put('/api/notifications/read-all');
    return response.data;
  },

  createNotification: async (data: any): Promise<any> => {
    const response = await api.post('/api/notifications', data);
    return response.data;
  },

  getDispatchExpiryAlerts: async (): Promise<any> => {
    const response = await api.get('/api/notifications/dispatch-expiry');
    return response.data;
  },

  // === 알림 시스템 (BACK-205) ===
  getAlerts: async (unreadOnly: boolean = false, limit: number = 50, offset: number = 0): Promise<any> => {
    const response = await api.get('/api/alerts', { params: { unread_only: unreadOnly, limit, offset } });
    return response.data;
  },

  getAlertCount: async (): Promise<any> => {
    const response = await api.get('/api/alerts/count');
    return response.data;
  },

  markAlertsRead: async (alertIds: number[]): Promise<any> => {
    const response = await api.post('/api/alerts/read', { alert_ids: alertIds });
    return response.data;
  },

  markAllAlertsRead: async (): Promise<any> => {
    const response = await api.post('/api/alerts/read-all');
    return response.data;
  },

  dismissAlert: async (alertId: number): Promise<any> => {
    const response = await api.post(`/api/alerts/${alertId}/dismiss`);
    return response.data;
  },

  generateAlerts: async (): Promise<any> => {
    const response = await api.post('/api/alerts/generate');
    return response.data;
  },

  // === 데이터 임포트 (BACK-191) ===
  previewImport: async (file: File, companyId: number = 1): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_id', String(companyId));
    const response = await api.post('/api/import/employees/preview', formData);
    return response.data;
  },

  executeImport: async (file: File, companyId: number = 1): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_id', String(companyId));
    const response = await api.post('/api/import/employees/execute', formData);
    return response.data;
  },

  importEmployeesPreview: async (formData: FormData): Promise<any> => {
    const response = await api.post('/api/import/employees/preview', formData);
    return response.data;
  },

  importEmployeesExecute: async (formData: FormData): Promise<any> => {
    const response = await api.post('/api/import/employees/execute', formData);
    return response.data;
  },
};
